import Feature from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import type Geometry from 'ol/geom/Geometry';
import type { CasurHydricClass, CasurHydricKind, CasurHydricReference, CasurHydricStatus, OriginalAttributes, VectorFeatureRecord, VectorLayerModel } from '../core/types';
import { createGenericLayer } from '../domain/layers';

const DATABASE_NAME = 'casur-geostudio-local-data';
const DATABASE_VERSION = 1;
const STORE_NAME = 'casur-hydric-reference';
const CURRENT_KEY = 'current';
const geojson = new GeoJSON();

interface StoredRecord {
  readonly id: string;
  readonly geometry: object;
  readonly attributes: OriginalAttributes;
  readonly hydricClass: CasurHydricClass;
  readonly hydricStatus: CasurHydricStatus;
  readonly label: string;
}

interface StoredLayer {
  readonly id: string;
  readonly name: string;
  readonly sourceName: string;
  readonly sourceCrs: string;
  readonly assumedCrs: boolean;
  readonly warnings: readonly string[];
  readonly records: readonly StoredRecord[];
}

interface StoredReference {
  readonly key: typeof CURRENT_KEY;
  readonly schemaVersion: 1;
  readonly channels: StoredLayer;
  readonly drainage: StoredLayer;
  readonly metadata: CasurHydricReference['metadata'];
}

export interface CasurHydricRepository {
  load(): Promise<CasurHydricReference | null>;
  save(reference: CasurHydricReference): Promise<void>;
}

const storedLayer = (layer: VectorLayerModel): StoredLayer => ({
  id: layer.id,
  name: layer.name,
  sourceName: layer.sourceName,
  sourceCrs: layer.sourceCrs,
  assumedCrs: layer.assumedCrs,
  warnings: layer.warnings,
  records: layer.features.map((record) => {
    const geometry = record.feature.getGeometry();
    if (!geometry) throw new Error(`La entidad ${record.id} no tiene geometría.`);
    return {
      id: record.id,
      geometry: geojson.writeGeometryObject(geometry),
      attributes: record.attributes,
      hydricClass: record.feature.get('casurHydricClass') as CasurHydricClass,
      hydricStatus: record.feature.get('casurHydricStatus') as CasurHydricStatus,
      label: String(record.feature.get('casurHydricLabel') ?? ''),
    };
  }),
});

export const serializeCasurHydricReference = (reference: CasurHydricReference): StoredReference => ({
  key: CURRENT_KEY,
  schemaVersion: 1,
  channels: storedLayer(reference.channels),
  drainage: storedLayer(reference.drainage),
  metadata: reference.metadata,
});

const restoredLayer = (stored: StoredLayer, kind: CasurHydricKind): VectorLayerModel => {
  const records: VectorFeatureRecord[] = stored.records.map((record) => {
    const geometry = geojson.readGeometry(record.geometry) as Geometry;
    const feature = new Feature({ geometry });
    feature.set('casurHydricKind', kind, true); feature.set('casurHydricId', record.id, true); feature.set('casurHydricClass', record.hydricClass, true); feature.set('casurHydricStatus', record.hydricStatus, true); feature.set('casurHydricLabel', record.label, true);
    return { id: record.id, feature, attributes: record.attributes };
  });
  return { ...createGenericLayer({ id: stored.id, name: stored.name, sourceName: stored.sourceName, sourceFormat: 'SHP', sourceCrs: stored.sourceCrs, assumedCrs: stored.assumedCrs, records, warnings: stored.warnings }), locked: true };
};

export const deserializeCasurHydricReference = (value: unknown): CasurHydricReference => {
  if (!value || typeof value !== 'object') throw new Error('La referencia CASUR local no es válida.');
  const stored = value as Partial<StoredReference>;
  if (stored.schemaVersion !== 1 || !stored.channels || !stored.drainage || !stored.metadata || !Array.isArray(stored.channels.records) || !Array.isArray(stored.drainage.records)) {
    throw new Error('La referencia CASUR local usa un formato incompatible o incompleto.');
  }
  return { channels: restoredLayer(stored.channels, 'channels'), drainage: restoredLayer(stored.drainage, 'drainage'), metadata: stored.metadata };
};

const requestResult = <T>(request: IDBRequest<T>): Promise<T> => new Promise((resolve, reject) => {
  request.addEventListener('success', () => resolve(request.result));
  request.addEventListener('error', () => reject(request.error ?? new Error('IndexedDB no pudo completar la operación.')));
});

const transactionDone = (transaction: IDBTransaction): Promise<void> => new Promise((resolve, reject) => {
  transaction.addEventListener('complete', () => resolve());
  transaction.addEventListener('abort', () => reject(transaction.error ?? new Error('La transacción local fue cancelada.')));
  transaction.addEventListener('error', () => reject(transaction.error ?? new Error('La transacción local falló.')));
});

export class IndexedDbCasurHydricRepository implements CasurHydricRepository {
  constructor(private readonly factory: IDBFactory | undefined = globalThis.indexedDB) {}

  private async database(): Promise<IDBDatabase> {
    if (!this.factory) throw new Error('Este navegador no ofrece IndexedDB para guardar la Referencia CASUR.');
    const request = this.factory.open(DATABASE_NAME, DATABASE_VERSION);
    request.addEventListener('upgradeneeded', () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) request.result.createObjectStore(STORE_NAME, { keyPath: 'key' });
    });
    return requestResult(request);
  }

  async load(): Promise<CasurHydricReference | null> {
    const database = await this.database();
    try {
      const transaction = database.transaction(STORE_NAME, 'readonly');
      const done = transactionDone(transaction);
      const value = await requestResult(transaction.objectStore(STORE_NAME).get(CURRENT_KEY));
      await done;
      return value === undefined ? null : deserializeCasurHydricReference(value);
    } finally { database.close(); }
  }

  async save(reference: CasurHydricReference): Promise<void> {
    const snapshot = serializeCasurHydricReference(reference);
    const database = await this.database();
    try {
      const transaction = database.transaction(STORE_NAME, 'readwrite');
      transaction.objectStore(STORE_NAME).put(snapshot);
      await transactionDone(transaction);
    } finally { database.close(); }
  }
}

export class MemoryCasurHydricRepository implements CasurHydricRepository {
  private value: ReturnType<typeof serializeCasurHydricReference> | null = null;
  failNextSave = false;
  async load(): Promise<CasurHydricReference | null> { return this.value ? deserializeCasurHydricReference(structuredClone(this.value)) : null; }
  async save(reference: CasurHydricReference): Promise<void> {
    if (this.failNextSave) { this.failNextSave = false; throw new Error('Fallo sintético al guardar.'); }
    this.value = structuredClone(serializeCasurHydricReference(reference));
  }
}
